import { IBP_TEMPLATE, ColumnDefinition, TemplateConfig, ColumnScope } from './ibp-template';

// Re-export template types for backward compatibility
export type { ColumnDefinition, TemplateConfig, ColumnScope };

// Get column keys and headers from template configuration
export const COLUMN_KEYS = IBP_TEMPLATE.columns.map(col => col.key) as ColumnKey[];
export const COLUMN_HEADERS: Record<ColumnKey, string> = {} as Record<ColumnKey, string>;
export const COLUMN_WIDTHS: Record<ColumnKey, number> = {} as Record<ColumnKey, number>;

// Populate COLUMN_HEADERS and COLUMN_WIDTHS from template
IBP_TEMPLATE.columns.forEach(col => {
  COLUMN_HEADERS[col.key as ColumnKey] = col.label;
  COLUMN_WIDTHS[col.key as ColumnKey] = col.width;
});

// Column key type derived from template
export type ColumnKey = 
  | 'gdoName'
  | 'sector'
  | 'market'
  | 'report'
  | 'origin'
  | 'destination'
  | 'timezone'
  | 'owner'
  | 'gdoDescription'
  | 'gdoCategory'
  | 'gdoDependentOn'
  | 'stageInboundOutbound'
  | 'gdoStep'
  | 'dataOwner'
  | 'dataSteward'
  | 'platform'
  | 'schedulerName'
  | 'jobName'
  | 'scheduleDays'
  | 'executionStartTime'
  | 'executionEndTime'
  | 'averageExecutionTime'
  | 'loadFrequency'
  | 'slaType'
  | 'slaDeadlineType'
  | 'slaDays'
  | 'slaStartTime'
  | 'slaEndTime'
  | 'slaCriteria'
  | 'slaEndDayOffset'
  | 'slaCriticality'
  | 'slaMaxRuntimeMinutes'
  | 'slaScheduleTrigger'
  | 'remarks'
  | 'forumsOrSystems'
  | 'inScope'
  | 'jobType'
  | 'release'
  | 'area'
  | 'pushPullMechanism'
  | 'timeLapseBetweenSourceUpdateAndLoadInLanding'
  | 'dependenciesWithOtherJobsReferSequence'
  | 'gdoDependencies'
  | 'jobOwner'
  | 'dataSourceOfExtraction'
  | 'sourceFilesTablesViews'
  | 'datasetType'
  | 'dataLoadingStrategy'
  | 'originalSourceDataAvailabilityCst'
  | 'eventOrScheduled'
  | 'errorHandlingAction'
  | 'jobEndTimeCst'
  | 'jobEndTimeUtc'
  | 'typeOfTransferJob'
  | 'hardGdoDependencies'
  | 'expectedExecutionWindows'
  | 'o9ErrorHandling'
  | 'pipelineName'
  | 'executionDate'
  | 'workingDayToBeExecuted'
  | 'jobGroup'
  | 'batchScope'
  | 'jobStartTimeCetCest'
  | 'jobEndTimeCetCest'
  | 'op'
  | 'dependancyWithEvent'
  | 'dependencyOnForun'
  | 'dateOfExecution'
  | 'month'
  | 'firstDayOfMonth'
  | 'dmrDate'
  | 'smrDate'
  | 'workingDay'
  | 'calenderDay'
  | 'fridayOfSmr'
  | 'mondayOfDmr'
  | 'forumDependency'
  | 'forumDependencyWorkingDay'
  | 'previousJob'
  | 'dataProviderForManualFiles'
  | 'itSourceContact'
  | 'originalExecutionDate'
  | 'updatedDate'
  | 'transferDuration'
  | 'mdtStartTime'
  | 'jobNotificationMethod'
  | 'batchLoadStartTime'
  | 'sourceOwnerContact'
  | 'factoryName'
  | 'workspaceName'
  | 'triggerName'
  | 'application'
  | 'folderPath';

export interface RawGDORow {
  id: number;
  gdoName: string;
  sector: string;
  market: string;
  report: string;
  origin: string;
  destination: string;
  timezone: string;
  owner: string;
  gdoDescription: string;
  gdoCategory: string;
  gdoDependentOn: string;
  stageInboundOutbound: string;
  gdoStep: string;
  dataOwner: string;
  dataSteward: string;
  platform: string;
  schedulerName: string;
  jobName: string;
  scheduleDays: string;
  executionStartTime: string;
  executionEndTime: string;
  averageExecutionTime: string;
  loadFrequency: string;
  slaType: string;
  slaDeadlineType: string;
  slaDays: string;
  slaStartTime: string;
  slaEndTime: string;
  slaCriteria: string;
  slaEndDayOffset: string;
  slaCriticality: string;
  slaMaxRuntimeMinutes: string;
  slaScheduleTrigger: string;
  remarks?: string;
  forumsOrSystems: string;
  inScope: string;
  jobType: string;
  release: string;
  area: string;
  pushPullMechanism: string;
  timeLapseBetweenSourceUpdateAndLoadInLanding: string;
  dependenciesWithOtherJobsReferSequence: string;
  gdoDependencies: string;
  jobOwner: string;
  dataSourceOfExtraction: string;
  sourceFilesTablesViews: string;
  datasetType: string;
  dataLoadingStrategy: string;
  originalSourceDataAvailabilityCst: string;
  eventOrScheduled: string;
  errorHandlingAction: string;
  jobEndTimeCst: string;
  jobEndTimeUtc: string;
  typeOfTransferJob: string;
  hardGdoDependencies: string;
  expectedExecutionWindows: string;
  o9ErrorHandling: string;
  pipelineName: string;
  executionDate: string;
  workingDayToBeExecuted: string;
  jobGroup: string;
  batchScope: string;
  jobStartTimeCetCest: string;
  jobEndTimeCetCest: string;
  op: string;
  dependancyWithEvent: string;
  dependencyOnForun: string;
  dateOfExecution: string;
  month: string;
  firstDayOfMonth: string;
  dmrDate: string;
  smrDate: string;
  workingDay: string;
  calenderDay: string;
  fridayOfSmr: string;
  mondayOfDmr: string;
  forumDependency: string;
  forumDependencyWorkingDay: string;
  previousJob: string;
  dataProviderForManualFiles: string;
  itSourceContact: string;
  originalExecutionDate: string;
  updatedDate: string;
  transferDuration: string;
  mdtStartTime: string;
  jobNotificationMethod: string;
  batchLoadStartTime: string;
  sourceOwnerContact: string;
  factoryName: string;
  workspaceName: string;
  triggerName: string;
  application: string;
  folderPath: string;
}

export interface ValidatedGDORow extends RawGDORow {
  isValid: boolean;
  cellErrors: Record<ColumnKey, string>;
  rowError?: string;
}

export interface ValidationRefData {
  sectors: string[];
  markets: string[];
  reports: string[];
  timezones: string[];
  existingGDONames: Set<string>;
  platformSchedulerMap: Record<string, string[]>;
}

export interface DisplayRow {
  type: 'group' | 'data';
  id: string | number;
  gdoName?: string;
  children?: ValidatedGDORow[];
  data?: ValidatedGDORow;
}

export interface UploadSummary {
  successCount: number;
  errorCount: number;
  skippedCount: number;
  successRows: ValidatedGDORow[];
  errorRows: { row: ValidatedGDORow; reason: string }[];
  skippedRows: ValidatedGDORow[];
  dependencySummary?: {
    groupsProcessed: number;
    added: number;
    removed: number;
    unchanged: number;
    cyclesDetected: number;
    errors: { gdoName: string; market: string; error: string }[];
  };
}

export type ModalStep = 'upload' | 'preview' | 'confirm' | 'uploading' | 'summary';

export interface GBulkUploadModalProps {
  open: boolean;
  onClose: () => void;
}

// Helper functions for template access
export function getColumnByKey(key: string): ColumnDefinition | undefined {
  return IBP_TEMPLATE.columns.find(col => col.key === key);
}

export function getRequiredColumns(): ColumnDefinition[] {
  return IBP_TEMPLATE.columns.filter(col => col.required);
}

export function getColumnsByScope(scope: ColumnScope): ColumnDefinition[] {
  return IBP_TEMPLATE.columns.filter(col => col.scope === scope);
}

export function getColumnLabels(): string[] {
  return IBP_TEMPLATE.columns.map(col => col.label);
}

export function getColumnWidths(): { wch: number }[] {
  return IBP_TEMPLATE.columns.map(col => ({ wch: col.width }));
}

export function getColumnComment(key: string, refData?: any): string {
  const col = getColumnByKey(key);
  if (!col) return '';
  
  if (typeof col.comment === 'function') {
    return col.comment(refData);
  }
  return col.comment;
}

export function getSampleValues(key: string): string[] {
  const col = getColumnByKey(key);
  return col?.sampleValues || [];
}
